/**
 * What "the server actually did it" means, one function per long mutation.
 *
 * They live together rather than next to each mutation for two reasons: it is the list a reader
 * needs to audit in one sitting (a weak verify is worse than none, because it invents a success),
 * and it keeps them testable without mounting a page.
 *
 * Three shared rules, learned from the case that started this:
 *
 *  1. NEVER a clock. Every "is this row mine" test is answered with an id the caller had not
 *     seen, never by comparing `created_at` to `Date.now()`. The reported failure happened on a
 *     laptop on an aeroplane; assuming the browser clock agrees with Postgres would make the
 *     check least reliable exactly where it is needed.
 *  2. THREE ANSWERS, not two. Landed (return the resource), definitely not landed (return null),
 *     or unanswerable (throw). Collapsing the third into either of the other two is how a
 *     verification mechanism starts lying, in one direction or the other.
 *  3. AMBIGUITY IS NOT SUCCESS. When the only candidate is a row that already existed before the
 *     click, the honest answer is "I cannot tell", because that is precisely the shape of the
 *     twin-exploration defect: two rows on the same corpus, indistinguishable on every exposed
 *     field.
 */
import { knowledgeBaseApi } from '@/lib/api/knowledge-base.api';
import { explorationApi } from '@/lib/api/exploration.api';
import { ontologyApi } from '@/lib/api/ontology.api';
import { versionApi, type OntologyVersion, type PublishOptions, type PublishResponse } from '@/lib/api/version.api';
import type { KBDocument } from '@shared/types/knowledge-base';
import type { CreateExplorationDto, KnowledgeExploration } from '@snxt/snxt-onto-share';

import type { VerifyFn } from './useResilientMutation';

/** Shared by every verify that needs to tell a fresh row from one that was already there. */
interface Baseline {
  /** Ids the caller could see when the request went out. */
  knownIds: ReadonlySet<string>;
  /** False when that list was never loaded, so "an id I had not seen" proves nothing. */
  snapshotAvailable: boolean;
}

// ─── Create exploration (the reported case) ───────────────────────────────────

export interface CreateExplorationVars extends Baseline {
  dto: CreateExplorationDto & { populate?: boolean };
}

/**
 * `POST /explorations` inserts the row, fires populate and forgets it, and answers 201 in about
 * half a second. So a timeout on this route almost always means the response was lost, and the
 * exploration is sitting there completed.
 *
 * The key is the tuple the request asked for, (knowledge_base_id, ontology_version_id):
 *  * no exploration on that pair at all -> conclusive, nothing landed;
 *  * one the caller had not seen -> conclusive, that is ours;
 *  * only rows that predate the click -> unanswerable.
 */
export const verifyExplorationCreated: VerifyFn<KnowledgeExploration, CreateExplorationVars> = async (
  vars,
  { signal },
) => {
  const list = await explorationApi.list(signal);
  const matches = list.filter(
    (e) =>
      e.knowledge_base_id === vars.dto.knowledge_base_id &&
      e.ontology_version_id === vars.dto.ontology_version_id,
  );
  if (matches.length === 0) return null;
  // The baseline check comes FIRST, and that ordering is load-bearing: with no baseline
  // `knownIds` is empty, so every candidate looks new and the other branch would report a
  // pre-existing exploration as the one we just asked for. Measured by the test that pins it.
  if (!vars.snapshotAvailable) {
    throw new Error('No baseline exploration list: cannot tell a new row from an existing one');
  }
  const unseen = matches.find((e) => !vars.knownIds.has(e.id));
  return unseen ?? null;
};

// ─── Document upload ─────────────────────────────────────────────────────────

export interface UploadVars extends Baseline {
  kbId: string;
  file: File;
}

/**
 * Filename alone is not enough: the same file can legitimately be uploaded twice, so a row
 * carrying that name may be an older one. The id has to be one the page had not seen.
 *
 * `uploadDocument` needs FormData and so calls `fetch` directly instead of going through the api
 * client, which means its network failures arrive as a bare TypeError. `isUnknownOutcome` matches
 * those on purpose.
 */
export const verifyDocumentUploaded: VerifyFn<KBDocument, UploadVars> = async (vars, { signal }) => {
  const docs = await knowledgeBaseApi.listDocuments(vars.kbId, signal);
  const sameName = docs.filter((d) => d.filename === vars.file.name);
  if (sameName.length === 0) return null;
  // Baseline first: an empty `knownIds` makes every row look new (see verifyExplorationCreated).
  if (!vars.snapshotAvailable) {
    throw new Error('No baseline document list: cannot tell a new upload from an existing row');
  }
  const unseen = sameName.find((d) => !vars.knownIds.has(d.id));
  return unseen ?? null;
};

// ─── Document delete (single and bulk) ───────────────────────────────────────

export interface DeleteDocumentVars {
  kbId: string;
  docId: string;
}

/**
 * The cleanest verify of the set, because a delete has no ambiguity to resolve: the row is either
 * in the list or it is not, so no baseline and no clock are needed. It also makes the bulk path
 * resumable, where one lost response used to abandon the rest of the queue silently.
 */
export const verifyDocumentDeleted: VerifyFn<{ deletedId: string }, DeleteDocumentVars> = async (
  vars,
  { signal },
) => {
  const docs = await knowledgeBaseApi.listDocuments(vars.kbId, signal);
  return docs.some((d) => d.id === vars.docId) ? null : { deletedId: vars.docId };
};

// ─── Retry (re-ingest) a knowledge object ────────────────────────────────────

export interface RetryDocumentVars {
  kbId: string;
  docId: string;
  /** The row's conversion_status when Retry was pressed. */
  statusBefore: string | null;
}

/**
 * The server resets the row before re-firing the pipeline, so a row that has moved off the status
 * it was on is proof the call landed.
 *
 * That proof only exists when the row was TERMINAL to begin with. Retrying a row that already
 * reads `pending` (the stuck case) produces a row that still reads `pending`, and nothing in the
 * exposed fields separates "reset a second ago" from "never moved". That case is reported as
 * unconfirmed rather than guessed, and it is cheap to live with: a second retry re-processes the
 * same knowledge object in place, it does not create a row.
 */
export const verifyRetryAccepted: VerifyFn<{ ok: true }, RetryDocumentVars> = async (
  vars,
  { signal },
) => {
  const docs = await knowledgeBaseApi.listDocuments(vars.kbId, signal);
  const row = docs.find((d) => d.id === vars.docId);
  if (!row) {
    throw new Error('Document is no longer listed: cannot tell whether the retry was accepted');
  }
  const wasTerminal = vars.statusBefore === 'failed' || vars.statusBefore === 'completed';
  if (!wasTerminal) {
    throw new Error('Row was already in flight: a retry leaves no observable trace to check');
  }
  return row.conversion_status !== vars.statusBefore ? { ok: true } : null;
};

// ─── Publish an ontology version ─────────────────────────────────────────────

export interface PublishVars extends Baseline {
  ontologyId: string;
  options: PublishOptions;
}

/**
 * Publish is the least repeatable mutation in the app: a second attempt burns a second number
 * and forks a second draft. So it is the one where a lost response really must not turn into a
 * second try.
 *
 * The requested number is not usable as the key, because the server decides it (a bump is applied
 * to the latest PUBLISHED version server-side, and a first publish is 0.1.0 whatever was asked).
 * What is checkable is the shape: a published row the caller had not seen, carrying a number. The
 * forked draft is read from the same list so the reconstructed response matches what the route
 * returns and the success path does not need to know which way the data arrived.
 */
export const verifyVersionPublished: VerifyFn<PublishResponse, PublishVars> = async (
  vars,
  { signal },
) => {
  const versions = await versionApi.list(vars.ontologyId, signal);
  const published = versions.filter((v) => v.status === 'published' && !vars.knownIds.has(v.id));
  if (published.length === 0) return null;
  if (!vars.snapshotAvailable) {
    throw new Error('No baseline version list: cannot tell a new published row from an old one');
  }
  // Newest first, by time. version_number is a string, so sorting on it would put "0.10.0"
  // before "0.9.0" (the trap called out in AGENTS.md).
  const newest = [...published].sort(
    (a, b) =>
      new Date(b.published_at ?? b.created_at).getTime() -
      new Date(a.published_at ?? a.created_at).getTime(),
  )[0];
  if (!newest.version_number) {
    // A published row with no number should not exist. Rather than cast it into the response
    // shape and let a null travel into the UI, refuse to answer.
    throw new Error('Published version has no number: refusing to report this as a success');
  }
  const nextDraft = versions.find((v) => v.status === 'draft' && !vars.knownIds.has(v.id));
  return {
    published: newest as OntologyVersion & { version_number: string },
    // Publishing forks a fresh draft. If the list does not show one, the published row is the
    // closest truthful thing to hand; callers only read `published`.
    next_draft: nextDraft ?? newest,
  };
};

// ─── Re-run populate ─────────────────────────────────────────────────────────

export interface RepopulateVars {
  explorationId: string;
  startedAtBefore: string | null;
  statusBefore: string | null;
}

/**
 * The route sets `populate_started_at` and flips the status before returning, so a row whose
 * start time has moved is proof the call landed. The status fallback covers the case where the
 * timestamp we had was already null and only the status has moved.
 *
 * When the row was ALREADY pending or running with the same start time there is nothing
 * observable to compare against, so this refuses to answer instead of guessing. The button is
 * disabled in that state, so this is the defensive branch, not the normal one.
 */
export const verifyPopulateStarted: VerifyFn<{ ok: true }, RepopulateVars> = async (
  vars,
  { signal },
) => {
  const row = await explorationApi.get(vars.explorationId, signal);
  if (row.populate_started_at !== vars.startedAtBefore) return { ok: true };
  const movedOffTerminal =
    (row.populate_status === 'pending' || row.populate_status === 'running') &&
    vars.statusBefore !== 'pending' &&
    vars.statusBefore !== 'running';
  if (movedOffTerminal) return { ok: true };
  if (vars.statusBefore === 'pending' || vars.statusBefore === 'running') {
    throw new Error('Populate was already in flight: a re-run leaves no observable trace to check');
  }
  return null;
};

// ─── Import an ontology from JSON-LD ─────────────────────────────────────────

export interface ImportOntologyVars extends Baseline {
  name: string;
  jsonld: string;
}

/**
 * What the import reports back. `counts` is absent when the response was lost and the outcome had
 * to be read from the ontology list, which does not carry node/edge counts. Saying "counts
 * unknown" is the honest version of recovering from a lost response; inventing zeros would not be.
 */
export interface ImportOntologyResult {
  id: string;
  name: string;
  counts: { nodes: number; edges: number } | null;
}

/**
 * The name is the only thing the caller controls (the server assigns the id and the version), so
 * the check is "an ontology carrying this name that the page had not seen". Names are not unique
 * in this schema, which is exactly why the unseen-id half is not optional.
 */
export const verifyOntologyImported: VerifyFn<ImportOntologyResult, ImportOntologyVars> = async (
  vars,
  { signal },
) => {
  const list = (await ontologyApi.getOntologies(signal)) as unknown as Array<{
    id: string;
    name: string;
  }>;
  const sameName = list.filter((o) => o.name === vars.name);
  if (sameName.length === 0) return null;
  // Baseline first: an empty `knownIds` makes every row look new (see verifyExplorationCreated).
  if (!vars.snapshotAvailable) {
    throw new Error('No baseline ontology list: cannot tell a new import from an existing one');
  }
  const unseen = sameName.find((o) => !vars.knownIds.has(o.id));
  return unseen ? { id: unseen.id, name: unseen.name, counts: null } : null;
};
